<template>
  <div
      :class="[
      'd-flex gmb-row',
      { 'align-items-center': !alignControlsOnTop },
      {
        'rounded-1': collapseId || isSelectable,
      },
      {
        'gmb-row--clickable': collapseId,
      },
      {
        'py-1 px-2':
          (!collapseId && !isSelectable) ||
          (!collapseId && isSelectable && isSelectableTextStyle),
      },
      {
        'p-2':
          (!collapseId && isSelectable && !isSelectableTextStyle) ||
          (collapseId && !isSelectable),
      },
      {
        'p-1': collapseId && isSelectable && isSelectableTextStyle,
      },
      {
        'gmb-row--selectable-text': isSelectable && isSelectableTextStyle,
      },
      {
        'gmb-row--selectable-area': isSelectable && !isSelectableTextStyle,
      },
      {
        'gmb-row--selected':
          isSelectable && (checkboxIsActivated || radioButtonIsActivated),
      },
    ]"
      :id="selfDomNodeId"
  >
    <!-- Caret button -->
    <div
        v-if="collapseId"
        :class="[
        'd-flex flex-column justify-content-center align-items-center gmb-row__offset-wrapper',
        { 'mt-1': alignControlsOnTop },
      ]"
    >
      <b-button
          v-b-toggle="collapseId"
          class="d-flex w-100 p-0 h-100 justify-content-center align-items-center text-size-reset rounded-1 btn-transparent-light"
          @click.stop
      >
        <i
            :class="['fas', 'fa-angle-' + (collapseIsVisible ? 'down' : 'right')]"
        />
      </b-button>
    </div>

    <!-- Select control -->
    <div
        v-if="isSelectable"
        :class="[
        'd-flex flex-column',
        { 'pl-1': collapseId },
        { 'mt-1': alignControlsOnTop },
      ]"
    >
      <div
          class="d-flex flex-column justify-content-center align-items-center gmb-row__offset-wrapper"
      >
        <GmbCheckbox
            v-if="isCheckbox"
            :switcherStyle="switcherStyle"
            :value="controlValue"
            ref="gmbCheckbox"
            @state-changed="(newState) => onCheckboxStateChanged(newState)"
        />

        <GmbRadioButton
            v-else-if="isRadioButton"
            ref="gmbRadioButton"
            :value="controlValue"
            @state-changed="(newState) => onRadioButtonStateChanged(newState)"
        />
      </div>
    </div>

    <!-- Picto -->
    <div
        v-if="hasPicto"
        :class="[
        'd-flex flex-column',
        { 'pl-1': collapseId || isSelectable },
        { 'mt-1': alignControlsOnTop },
      ]"
    >
      <div
          class="d-flex flex-column justify-content-center align-items-center gmb-row__offset-wrapper"
      >
        <slot name="picto"/>
      </div>
    </div>

    <!-- Main -->
    <div
        :class="[
        'd-flex flex-grow-1 w-0 flex-column',
        { 'pl-1': collapseId || isSelectable || hasPicto },
      ]"
    >
      <slot/>
    </div>

    <!-- Info sticker -->
    <div v-if="hasInfoSticker" class="d-flex flex-column pl-1">
      <div
          :class="[
          'd-flex flex-column justify-content-end align-items-center gmb-row__offset-wrapper',
          isStickerOffsetWidthFixed
            ? 'gmb-row__offset-wrapper--large-fixed'
            : 'gmb-row__offset-wrapper--large',
        ]"
      >
        <slot name="infoSticker"/>
      </div>
    </div>
  </div>
</template>

<script>
import {hasEnrichedSlot} from "@/helpers.js";

import GmbCheckbox from "@/widgets/Checkbox";
import GmbRadioButton from "@/widgets/RadioButton";

export default {
  name: "GmbRow",

  components: {
    GmbCheckbox,
    GmbRadioButton,
  },

  props: {
    alignControlsOnTop: Boolean,
    collapseId: String,
    collapseIsVisible: Boolean,
    controlGroupId: String,
    controlValue: String,
    isCheckbox: Boolean,
    isRadioButton: Boolean,
    isSelectableTextStyle: Boolean,
    isStickerOffsetWidthFixed: Boolean,
    switcherStyle: Boolean,
  },

  data() {
    return {
      checkboxIsActivated: undefined,
      isSelectable: false,
      radioButtonIsActivated: undefined,
    };
  },

  computed: {
    hasInfoSticker() {
      return hasEnrichedSlot(this, "infoSticker");
    },

    hasPicto() {
      return hasEnrichedSlot(this, "picto");
    },

    selfDomNodeId() {
      return this.controlGroupId
          ? this.controlGroupId + "-" + this.controlValue
          : this.controlValue;
    },
  },

  watch: {},

  beforeCreate() {
  },

  created() {
    this.isSelectable = this.isCheckbox || this.isRadioButton;
  },

  beforeMount() {
  },

  mounted() {
    if (this.isCheckbox)
      this.checkboxIsActivated = this.$refs.gmbCheckbox.isActivated;

    if (this.isRadioButton)
      this.radioButtonIsActivated = this.$refs.gmbRadioButton.isActivated;
  },

  beforeUpdate() {
  },

  updated() {
  },

  beforeUnmount() {
  },

  unmounted() {
  },

  methods: {
    onCheckboxStateChanged(newState) {
      this.checkboxIsActivated = newState;
      this.$emit("checkbox-state-changed", this.checkboxIsActivated);
    },

    onRadioButtonStateChanged(newState) {
      this.radioButtonIsActivated = newState;
      this.$emit("radio-button-state-changed", this.radioButtonIsActivated);
    },
  },
};
</script>

<style lang="scss">
@import "@/sass/settings/colors";
@import "@/sass/settings/dimensions";
@import "@/sass/bootstrap-overloads/colors";
@import "@/../node_modules/bootstrap/scss/functions";
@import "@/../node_modules/bootstrap/scss/mixins";
@import "@/../node_modules/bootstrap/scss/variables";
@import "@/../node_modules/bootstrap/scss/utilities/borders";
@import "@/../node_modules/bootstrap/scss/utilities/spacing";
@import "@/sass/settings/colors";

/* =========================================================================
 * ROW
 * ========================================================================= */

.gmb-row {
  @extend .border;

  border-color: transparent !important;

  &--clickable {
    &:hover {
      border-color: $border-color !important;
      // background-color: $border-color !important;
      cursor: pointer;
    }
  }

  &--selectable-area {
    background-color: rgba($gmb-color-theme-2, 0.2);

    &.gmb-row--selected {
      background-color: rgba($gmb-color-theme-2, 0.6);
    }
  }

  &--selectable-text {
    &.gmb-row--selected {
    }
  }

  &__offset-wrapper {
    min-height: $gmb-offset-3;

    &--large {
      max-width: 120px;
    }

    &:not(.gmb-row__offset-wrapper--large) {
      width: $gmb-offset-3;
    }

    &--large-fixed {
      width: 64px !important;
    }
  }
}
</style>
